import { useWishListStore } from '~/stores/wishList'
import { WebStorage } from '~/utils/WebStorage'
import { idsForLogin } from '~/utils/guestWishlistSync'

/**
 * Wishlist persistence and portal visibility.
 *
 * Logged in: server is source of truth; localStorage is cache + anonymous draft.
 * Logout keeps localStorage (do not write an empty SET as a side-effect).
 * Account switch uses the logout snapshot so account A's hearts are not merged
 * into account B — only hearts added after logout are kept.
 */
export default defineNuxtPlugin(() => {
  const webStorage = new WebStorage()
  const wishListStore = useWishListStore()
  const { user } = useGuest()
  const guestLoginEnabled = useGuestLoginEnabled()
  const { log } = useGuestActivityLog()

  const LAST_TRACKING_KEY = 'GuestWishlistLastTrackingId'
  const SNAPSHOT_KEY = 'GuestWishlistLogoutSnapshot'

  // Ids saved by the visitor but not resolvable on this portal.
  let foreignIds: number[] = []

  const VISIBILITY_CACHE_KEY = 'WishListVisibility'

  const allIds = (): number[] => Array.from(new Set([...wishListStore.items, ...foreignIds]))
  const signatureOf = (ids: number[]): string => [...ids].sort((a, b) => a - b).join(',')

  let prevItemsKey = signatureOf(allIds())
  let syncTimer: ReturnType<typeof setTimeout> | null = null
  let hydrating = false
  let loggedInForSync = false
  const SYNC_DELAY = 500

  function readLastTrackingId (): string | null {
    try {
      return localStorage.getItem(LAST_TRACKING_KEY)
    } catch {
      return null
    }
  }

  function writeLastTrackingId (id: string | null): void {
    try {
      if (id) {
        localStorage.setItem(LAST_TRACKING_KEY, id)
      } else {
        localStorage.removeItem(LAST_TRACKING_KEY)
      }
    } catch {
      // ignore
    }
  }

  function readSnapshotIds (): number[] {
    try {
      const raw = localStorage.getItem(SNAPSHOT_KEY)
      if (!raw) {
        return []
      }
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.map(Number).filter(id => id > 0) : []
    } catch {
      return []
    }
  }

  function writeSnapshotIds (ids: number[]): void {
    try {
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(ids))
    } catch {
      // ignore
    }
  }

  function clearSnapshotIds (): void {
    try {
      localStorage.removeItem(SNAPSHOT_KEY)
    } catch {
      // ignore
    }
  }

  function cancelBackendSync (): void {
    if (syncTimer) {
      clearTimeout(syncTimer)
      syncTimer = null
    }
  }

  function scheduleBackendSync (): void {
    if (!loggedInForSync || hydrating) {
      return
    }
    cancelBackendSync()
    syncTimer = setTimeout(async () => {
      syncTimer = null
      if (!loggedInForSync || hydrating) {
        return
      }
      try {
        await useNuxtApp().$backend.guestWishlist.setWishlistFromClient(allIds())
      } catch {
        log('wishlist_sync_fail')
      }
    }, SYNC_DELAY)
  }

  async function partitionByPortalVisibility () {
    const ids = allIds()
    if (ids.length === 0) {
      return
    }
    try {
      const response = await useNuxtApp().$backend.search.searchFromClient({
        rows: ids.length,
        only_object_ids: ids
      })
      const resolved = new Set(
        (response?.data?.normal_results?.plain || [])
          .map((item: { nr?: number | string }) => Number(item?.nr))
          .filter((nr: number) => nr > 0)
      )
      foreignIds = ids.filter(id => !resolved.has(id))
      wishListStore.setItems(ids.filter(id => resolved.has(id)))
      try {
        sessionStorage.setItem(VISIBILITY_CACHE_KEY, JSON.stringify({
          checked: signatureOf(ids),
          foreign: foreignIds
        }))
      } catch {
        // sessionStorage unavailable - the check simply runs again next load
      }
    } catch {
      // Search unavailable - keep all ids rather than wrongly hiding hearts.
    }
  }

  function applyCachedVisibilityOrCheck () {
    const ids = allIds()
    if (ids.length === 0) {
      return
    }
    try {
      const cached = JSON.parse(sessionStorage.getItem(VISIBILITY_CACHE_KEY) || 'null')
      if (cached && cached.checked === signatureOf(ids) && Array.isArray(cached.foreign)) {
        foreignIds = cached.foreign.filter((id: number) => ids.includes(id))
        wishListStore.setItems(ids.filter(id => !foreignIds.includes(id)))
        return
      }
    } catch {
      // Broken cache entry - fall through to a fresh check
    }
    partitionByPortalVisibility()
  }

  function persistLocal (): void {
    const itemsForWebStorage: Record<string, number> = {}
    for (const objektNr of allIds()) {
      itemsForWebStorage['obj_' + objektNr] = Math.round(Date.now() / 1000)
    }
    webStorage.items.WishList.set(itemsForWebStorage)
    webStorage.items.WishList_ShareKey.set(wishListStore.share_key)
  }

  watch(
    wishListStore.$state,
    () => {
      persistLocal()

      const currentItemsKey = signatureOf(allIds())
      if (currentItemsKey !== prevItemsKey) {
        prevItemsKey = currentItemsKey
        if (guestLoginEnabled && loggedInForSync) {
          scheduleBackendSync()
        }
      }
    },
    { deep: true }
  )

  applyCachedVisibilityOrCheck()

  let hydrateGen = 0

  async function hydrateFromBackend (trackingId: string) {
    const gen = ++hydrateGen
    hydrating = true
    loggedInForSync = true
    try {
      const response = await useNuxtApp().$backend.guestWishlist.getWishlistFromClient()
      if (gen !== hydrateGen) {
        return
      }
      const serverIds = response.data.object_ids
        ? response.data.object_ids.split(',').filter(Boolean).map(Number)
        : []
      const merged = idsForLogin({
        localIds: allIds(),
        serverIds,
        currentTrackingId: trackingId,
        lastTrackingId: readLastTrackingId(),
        snapshotIds: readSnapshotIds()
      })
      foreignIds = []
      wishListStore.setItems(merged)
      await partitionByPortalVisibility()
      if (gen !== hydrateGen) {
        return
      }
      prevItemsKey = signatureOf(allIds())
      persistLocal()
      try {
        await useNuxtApp().$backend.guestWishlist.setWishlistFromClient(allIds())
      } catch {
        log('wishlist_sync_fail')
      }
      log('wishlist_merge_on_login', { id_count: allIds().length })
      writeLastTrackingId(trackingId)
      clearSnapshotIds()
    } catch {
      if (gen === hydrateGen) {
        log('wishlist_sync_fail')
        writeLastTrackingId(trackingId)
      }
    } finally {
      if (gen === hydrateGen) {
        hydrating = false
        if (loggedInForSync) {
          scheduleBackendSync()
        }
      }
    }
  }

  function onLogout (previousTrackingId: string): void {
    writeSnapshotIds(allIds())
    writeLastTrackingId(previousTrackingId)
    loggedInForSync = false
    cancelBackendSync()
  }

  if (guestLoginEnabled) {
    watch(
      () => user.value?.tracking_id ?? null,
      (next, prev) => {
        if (prev && !next) {
          onLogout(prev)
          return
        }
        if (next && next !== prev) {
          hydrateFromBackend(next)
        }
      }
    )

    if (user.value?.tracking_id) {
      hydrateFromBackend(user.value.tracking_id)
    }
  }
})
